/**
 * Utility moderna per gestire query string con immutabilità e chainability
 * Usa URLSearchParams (API nativa) per parsing e serializzazione
 *
 * @class QueryStringManager
 * @example
 * const qs = new QueryStringManager('?page=1&category=tech&tags=react&tags=nextjs');
 * qs.get('page') // '1'
 * qs.get('tags') // ['react', 'nextjs']
 * qs.set('page', '2').remove('category').toString() // 'page=2&tags=react&tags=nextjs'
 */
class QueryStringManager {
  /**
   * @param {string} [queryString=''] - Query string da parsare (con o senza '?')
   */
  constructor(queryString = '') {
    this._query = this._parseQueryString(queryString)
    this._stringCache = null
  }

  /**
   * Parsa una query string in un oggetto normalizzato
   * Gestisce parametri duplicati convertendoli in array
   *
   * @private
   * @param {string} qs - Query string da parsare
   * @returns {QueryObject} Oggetto normalizzato con parametri
   */
  _parseQueryString(qs) {
    if (!qs || typeof qs !== 'string') return {}

    // Rimuove il leading '?'
    const cleanQueryString = qs.startsWith('?') ? qs.slice(1) : qs
    if (!cleanQueryString) return {}

    const params = new URLSearchParams(cleanQueryString)
    const result = {}

    for (const [key, value] of params.entries()) {
      if (result[key] === undefined) {
        result[key] = value
      } else {
        // Converte a array se ci sono duplicati (mantenendo l'ordine)
        result[key] = Array.isArray(result[key])
          ? [...result[key], value]
          : [result[key], value]
      }
    }

    return result
  }

  /**
   * Invalida la cache della stringa
   * @private
   */
  _invalidateCache() {
    this._stringCache = null
  }

  /**
   * Crea una copia profonda dell'istanza (immutabilità)
   * @private
   * @returns {QueryStringManager} Nuova istanza con gli stessi parametri
   */
  _clone() {
    const cloned = new QueryStringManager()
    cloned._query = JSON.parse(JSON.stringify(this._query))
    return cloned
  }

  /**
   * Verifica se ci sono parametri nella query
   * @returns {boolean}
   */
  hasQuery() {
    return Object.keys(this._query).length > 0
  }

  /**
   * Ottiene il numero di parametri
   * @returns {number}
   */
  size() {
    return Object.keys(this._query).length
  }

  /**
   * Converte l'oggetto query in stringa (cached per performance)
   * @returns {string} Query string senza il '?'
   */
  toString() {
    if (this._stringCache !== null) return this._stringCache

    const params = new URLSearchParams()

    for (const [key, value] of Object.entries(this._query)) {
      if (Array.isArray(value)) {
        value.forEach((v) => {
          if (v !== null && v !== undefined) params.append(key, String(v))
        })
      } else if (value !== null && value !== undefined) {
        params.set(key, String(value))
      }
    }

    this._stringCache = params.toString()
    return this._stringCache
  }

  /**
   * Ottiene la query string con il leading '?'
   * @returns {string} Query string con '?' se contiene parametri, stringa vuota altrimenti
   */
  toSearchString() {
    const qs = this.toString()
    return qs ? `?${qs}` : ''
  }

  /**
   * Ottiene un valore specifico
   * @param {string} key - Chiave del parametro
   * @returns {QueryValue | QueryValue[] | null} Valore del parametro o null se non trovato
   */
  get(key) {
    return this._query[key] ?? null
  }

  /**
   * Ottiene un valore specifico garantendo un tipo
   * @param {string} key - Chiave del parametro
   * @param {QueryValue} [defaultValue=null] - Valore di default se non trovato o invalid
   * @returns {QueryValue} Valore del parametro o defaultValue
   */
  getString(key, defaultValue = null) {
    const value = this.get(key)
    if (Array.isArray(value)) return value[0] ?? defaultValue
    return value ?? defaultValue
  }

  /**
   * Aggiunge o modifica un parametro (chainable, immutable)
   * @param {string} key - Chiave del parametro
   * @param {QueryValue | QueryValue[]} value - Valore da impostare
   * @returns {QueryStringManager} Nuova istanza con il parametro modificato
   */
  set(key, value) {
    const cloned = this._clone()
    cloned._query[key] = value
    cloned._invalidateCache()
    return cloned
  }

  /**
   * Aggiunge un valore a un parametro array
   * @param {string} key - Chiave del parametro
   * @param {QueryValue} value - Valore da aggiungere
   * @returns {QueryStringManager} Nuova istanza con il valore aggiunto
   */
  append(key, value) {
    const cloned = this._clone()
    const current = cloned._query[key]

    if (current === undefined) {
      cloned._query[key] = value
    } else if (Array.isArray(current)) {
      cloned._query[key] = [...current, value]
    } else {
      cloned._query[key] = [current, value]
    }

    cloned._invalidateCache()
    return cloned
  }

  /**
   * Rimuove un parametro (chainable, immutable)
   * @param {string} key - Chiave del parametro da rimuovere
   * @returns {QueryStringManager} Nuova istanza con il parametro rimosso
   */
  remove(key) {
    const cloned = this._clone()
    delete cloned._query[key]
    cloned._invalidateCache()
    return cloned
  }

  /**
   * Rimuove un valore specifico da un parametro array
   * @param {string} key - Chiave del parametro
   * @param {QueryValue} value - Valore da rimuovere
   * @returns {QueryStringManager} Nuova istanza con il valore rimosso
   */
  removeValue(key, value) {
    const cloned = this._clone()
    const current = cloned._query[key]

    if (Array.isArray(current)) {
      cloned._query[key] = current.filter((v) => v !== value)
      if (cloned._query[key].length === 0) {
        delete cloned._query[key]
      } else if (cloned._query[key].length === 1) {
        cloned._query[key] = cloned._query[key][0]
      }
    }

    cloned._invalidateCache()
    return cloned
  }

  /**
   * Pulisce tutti i parametri
   * @returns {QueryStringManager} Nuova istanza vuota
   */
  clear() {
    const cloned = new QueryStringManager()
    return cloned
  }

  /**
   * Fonde i parametri con un altro oggetto o QueryStringManager
   * @param {QueryObject | QueryStringManager} other - Oggetto o QueryStringManager da fondere
   * @param {boolean} [merge=false] - Se true, unisce gli array; se false, sovrascrive
   * @returns {QueryStringManager} Nuova istanza con i parametri fusi
   */
  merge(other, merge = false) {
    const cloned = this._clone()
    const otherQuery =
      other instanceof QueryStringManager ? other._query : other

    for (const [key, value] of Object.entries(otherQuery)) {
      if (!merge || !(key in cloned._query)) {
        cloned._query[key] = value
      } else {
        // Modalità merge: combina gli array
        const current = cloned._query[key]
        const otherVal = Array.isArray(value) ? value : [value]
        const currentArr = Array.isArray(current) ? current : [current]
        cloned._query[key] = [...currentArr, ...otherVal]
      }
    }

    cloned._invalidateCache()
    return cloned
  }

  /**
   * Ottiene tutti i parametri come oggetto
   * @returns {QueryObject} Copia dell'oggetto query interno
   */
  getAll() {
    return JSON.parse(JSON.stringify(this._query))
  }

  /**
   * Ottiene le chiavi di tutti i parametri
   * @returns {string[]} Array di chiavi
   */
  keys() {
    return Object.keys(this._query)
  }

  /**
   * Ottiene i valori di tutti i parametri
   * @returns {(QueryValue | QueryValue[])[]} Array di valori
   */
  values() {
    return Object.values(this._query)
  }

  /**
   * Ottiene le coppie [chiave, valore]
   * @returns {[string, QueryValue | QueryValue[]][]} Array di coppie
   */
  entries() {
    return Object.entries(this._query)
  }

  /**
   * Itera su ogni parametro
   * @param {(value: QueryValue | QueryValue[], key: string) => void} callback - Funzione di callback
   * @returns {void}
   */
  forEach(callback) {
    for (const [key, value] of this.entries()) {
      callback(value, key)
    }
  }

  /**
   * Verifica se un parametro esiste
   * @param {string} key - Chiave del parametro
   * @returns {boolean}
   */
  has(key) {
    return key in this._query
  }

  /**
   * Crea un'istanza da una URL completa
   * @static
   * @param {string} url - URL completa
   * @returns {QueryStringManager} Nuova istanza con i parametri dell'URL
   */
  static fromURL(url) {
    const urlObj = new URL(url, 'http://localhost')
    return new QueryStringManager(urlObj.search)
  }

  /**
   * Crea un'istanza da un oggetto
   * @static
   * @param {QueryObject} obj - Oggetto con parametri
   * @returns {QueryStringManager} Nuova istanza
   */
  static fromObject(obj) {
    const qs = new QueryStringManager()
    qs._query = JSON.parse(JSON.stringify(obj))
    return qs
  }
}

export default QueryStringManager
export { QueryStringManager }
